import { useLayoutEffect, useMemo, useRef } from 'react';
import { isAxiosError, type AxiosError } from 'axios';
import type { ViewSnapshot } from '@api/apps';
import { getViewRenderOffer, renderView } from '@api/viewRender';
import { SandboxRequestError, type SandboxBridge, type SandboxReply } from '@components/HtmlSandboxViewer';
import { fetchArtifactBlob } from '@hooks/useModelBlob';
import { isOwnApiUrl } from '@utils/apiOrigin';
import { triggerAuthDownload } from '@utils/authDownload';
import {
  PAID_VIEW_METHODS,
  RUN_METHODS,
  SPENDING_METHODS,
  type KitErrorCode,
  type KitMethod,
  type KitState,
} from '../../../appKit/host';
import { StreamRequestError } from '../useAppRunStream';
import { answerParamsOf, fileIdOf, refineParamsOf, startParamsOf, viewRenderParamsOf } from './bridgeParams';
import type { FileIndex } from './bridgeState';
import { NoRun, TurnInFlight, type AppFrameController } from './useAppRunController';

export const SPEND_BURST = 5;
export const SPEND_REFILL_MS = 6_000;

interface Bucket {
  tokens: number;
  updatedAt: number;
}

// Per app for the whole tab, so a page cannot win a fresh allowance by reloading itself.
const spendBuckets = new Map<string, Bucket>();

const takeSpendToken = (appKey: string, now = Date.now()): boolean => {
  const bucket = spendBuckets.get(appKey) ?? { tokens: SPEND_BURST, updatedAt: now };
  const refilled = Math.min(SPEND_BURST, bucket.tokens + (now - bucket.updatedAt) / SPEND_REFILL_MS);
  const allowed = refilled >= 1;
  spendBuckets.set(appKey, { tokens: allowed ? refilled - 1 : refilled, updatedAt: now });
  return allowed;
};

export const resetSpendBuckets = (): void => spendBuckets.clear();

/**
 * The host's view of whether the user just acted. Activation in the frame
 * reaches its ancestors, and the page cannot fake it because this runs in the
 * host's own realm. A browser without the API is let through: the turn lock,
 * the token bucket and the server's own limit still apply.
 */
const userJustActed = (): boolean => navigator.userActivation?.isActive ?? true;

const STATUS_CODES: Readonly<Record<number, KitErrorCode>> = {
  400: 'INVALID',
  402: 'NO_CREDITS',
  403: 'READ_ONLY',
  404: 'NOT_FOUND',
  409: 'BUSY',
  413: 'TOO_LARGE',
  429: 'RATE_LIMITED',
};

/** Thrown as is, or kept as the cause of a friendlier error by the API client. */
const httpFailureOf = (error: unknown): AxiosError | undefined => {
  if (isAxiosError(error)) return error;
  if (error instanceof Error && isAxiosError(error.cause)) return error.cause;
  return undefined;
};

const messageOf = (error: unknown): string => {
  const message = (httpFailureOf(error)?.response?.data as { message?: unknown } | undefined)?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return error instanceof Error && error.message.trim() ? error.message : 'That did not work.';
};

const statusOf = (error: unknown): number | undefined => {
  const failure = httpFailureOf(error);
  if (failure) return failure.response?.status;
  return error instanceof StreamRequestError ? error.status : undefined;
};

export const toRequestError = (error: unknown): SandboxRequestError => {
  if (error instanceof SandboxRequestError) return error;
  if (error instanceof TurnInFlight) return new SandboxRequestError('BUSY', error.message);
  if (error instanceof NoRun) return new SandboxRequestError('NO_RUN', error.message);
  const status = statusOf(error);
  return new SandboxRequestError((status && STATUS_CODES[status]) || 'FAILED', messageOf(error));
};

// Everything the page asks for may be refused; a refusal is an answer to the page, never a sign-out.
const PAGE_REQUEST = { skipAuthRedirect: true } as const;

const done = (value: unknown = null, transfer?: Transferable[]): SandboxReply => ({ value, transfer });

interface UseAppBridgeOptions {
  /** Null for the builder's preview, which runs nothing. */
  controller: AppFrameController | null;
  state: KitState | null;
  files: FileIndex;
  captureView: () => Promise<ViewSnapshot | undefined>;
  maxFileBytes: number;
  maxSessionBytes: number;
}

/**
 * What an app's page may ask of the host. Every call is checked as if hostile:
 * the method must be one of the kit's, its parameters must fit the manifest,
 * a file must be one the page was told about, and anything that spends the
 * user's credits needs the user to have just acted and is rate limited.
 */
export const useAppBridge = ({
  controller,
  state,
  files,
  captureView,
  maxFileBytes,
  maxSessionBytes,
}: UseAppBridgeOptions): SandboxBridge & { strike: () => void } => {
  const live = useRef({ controller, files, captureView });
  useLayoutEffect(() => {
    live.current = { controller, files, captureView };
  }, [controller, files, captureView]);
  const session = useRef({ bytesRead: 0, struck: false });

  return useMemo(() => {
    const fileOf = (raw: unknown) => {
      const id = fileIdOf(raw);
      const file = live.current.files.get(id);
      if (!file) throw new SandboxRequestError('NOT_FOUND', 'This page was not given that file.');
      return file;
    };

    const readFile = async (raw: unknown): Promise<SandboxReply> => {
      if (session.current.struck) {
        throw new SandboxRequestError('UNSUPPORTED', 'This page tried to leave, so it can no longer read files.');
      }
      const file = fileOf(raw);
      const tooLarge = (size: number) => size > maxFileBytes || session.current.bytesRead + size > maxSessionBytes;
      const refusal = () =>
        new SandboxRequestError('TOO_LARGE', `${file.name} is too large to open here; download it instead.`);
      // Checked before fetching too, so a page cannot make the host download a file it would refuse.
      if (file.size !== null && tooLarge(file.size)) throw refusal();
      const blob = await fetchArtifactBlob(file.url, isOwnApiUrl(file.url), PAGE_REQUEST);
      if (tooLarge(blob.size)) throw refusal();
      session.current.bytesRead += blob.size;
      const bytes = await blob.arrayBuffer();
      return done(bytes, [bytes]);
    };

    const spend = async (method: KitMethod, params: unknown): Promise<SandboxReply> => {
      const { controller: current, captureView: capture } = live.current;
      if (!current) {
        throw new SandboxRequestError('UNSUPPORTED', 'This is a preview. Start a test run to try the app.');
      }
      if (current.readOnly) throw new SandboxRequestError('READ_ONLY', 'This run belongs to someone else.');
      if (!current.manifest) throw new SandboxRequestError('NO_RUN', 'This app is still loading.');
      switch (method) {
        case 'start':
          await current.startRun(startParamsOf(params, current.manifest, current.run));
          return done();
        case 'refine': {
          const refinement = refineParamsOf(params);
          const viewSnapshot = refinement.marks?.length ? undefined : await capture();
          await current.refineRun({ ...refinement, viewSnapshot });
          return done();
        }
        default:
          await current.answerRun({ ...answerParamsOf(params), viewSnapshot: await capture() });
          return done();
      }
    };

    return {
      state,
      strike: () => {
        session.current.struck = true;
      },
      handle: (method: KitMethod, params: unknown): Promise<SandboxReply> => {
        const { controller: current } = live.current;
        if (state?.app.usesAgent === false && RUN_METHODS.includes(method)) {
          return Promise.reject(new SandboxRequestError('UNSUPPORTED', 'This app does not run the agent.'));
        }
        // Checked before anything awaits, while the activation is still current.
        const paid = SPENDING_METHODS.includes(method) ? current !== null : PAID_VIEW_METHODS.includes(method);
        if (paid) {
          if (!userJustActed()) {
            return Promise.reject(
              new SandboxRequestError('NEEDS_GESTURE', 'A run can only start from something the user just did.'),
            );
          }
          if (!takeSpendToken(current?.appKey ?? '')) {
            return Promise.reject(new SandboxRequestError('RATE_LIMITED', 'Too many runs at once. Wait a moment.'));
          }
        }
        const work = async (): Promise<SandboxReply> => {
          switch (method) {
            case 'start':
            case 'refine':
            case 'answer':
              return spend(method, params);
            case 'cancel':
              if (!current) throw new NoRun();
              await current.cancelRun();
              return done();
            case 'readFile':
              return readFile(params);
            case 'download': {
              const file = fileOf(params);
              // Fetched and saved under its name: a presigned link the browser can display would
              // otherwise open in place of the app.
              await triggerAuthDownload(file.url, file.name, { ...PAGE_REQUEST, observable: true });
              return done();
            }
            case 'openTranscript':
              current?.openTranscript();
              return done();
            case 'newRun':
              current?.newRun();
              return done();
            case 'viewRenderOffer':
              if (current?.requireAccount) throw new SandboxRequestError('UNSUPPORTED', 'AI renders need an account.');
              return done(await getViewRenderOffer());
            case 'renderView':
              if (current?.requireAccount) throw new SandboxRequestError('UNSUPPORTED', 'AI renders need an account.');
              return done(await renderView(viewRenderParamsOf(params)));
            case 'requireAccount':
              current?.requireAccount?.();
              return done();
            default:
              throw new SandboxRequestError('UNSUPPORTED', 'This page cannot ask for that.');
          }
        };
        return work().catch((error: unknown) => {
          throw toRequestError(error);
        });
      },
    };
  }, [state, maxFileBytes, maxSessionBytes]);
};
